import { useEffect, useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { userService } from '@chartai/firebase';

import { userKeys } from '../queryKeys';

export function useUserDocument(uid: string | null) {
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!uid) {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = userService.subscribeToUser(uid, (userData) => {
      setData(userData);
      setIsLoading(false);
    });

    return unsubscribe;
  }, [uid]);

  return { data, isLoading };
}

export function useCreateUser() {
  return useMutation({
    mutationFn: async (input: { uid: string; data: Record<string, unknown> }) => {
      await userService.createUser(input.uid, input.data);
    },
  });
}

export function useUpdateUser() {
  return useMutation({
    mutationFn: async (input: { uid: string; data: Record<string, unknown> }) => {
      await userService.updateUser(input.uid, input.data);
    },
  });
}

export function useTerminateAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (uid: string) => {
      await userService.terminateAccount(uid);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
